import React from 'react';
import { Avatar } from '../Avatar/Avatar';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { ViewportDrawer } from '../Overlay';
import { CALL_REASON_META } from '../../calls/mockData';
import type { CallRow } from '../../calls/types';

export interface CallCardProps {
  row: CallRow | null;
  /** True when this card was opened by pressing Call (the calling state runs). */
  calling: boolean;
  callingSince?: string;
  onClose: () => void;
  onStartCall: (row: CallRow) => void;
  onLogCall: (row: CallRow) => void;
  onOpenPatient: (row: CallRow) => void;
  onOpenReason: (row: CallRow, href: string) => void;
  onOpenCommunications: (row: CallRow) => void;
}

/** An uppercase section label, matching the AI Receptionist call-logs drawer. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">{children}</div>;
}

/** A tinted wash from a reason's strong colour, used for the card head. */
const wash = (c: string, pct: number) => `color-mix(in srgb, ${c} ${String(pct)}%, transparent)`;

/**
 * The call card (spec 31 §4, board CL2CallCard) — a right drawer over the dimmed
 * list, built on the shared ViewportDrawer so it never clips under the top bar and
 * matches the AI Receptionist call-logs drawer (2 Oct, polish pass 3 Oct): a roomy
 * avatar header with status pills and a meta line, a primary call action with
 * quiet links beside it, then one softly-tinted card per reason, and a bordered
 * "what's happened" timeline.
 */
export function CallCard(props: CallCardProps) {
  const { row, calling, callingSince, onClose, onStartCall, onLogCall, onOpenPatient, onOpenReason, onOpenCommunications } = props;

  return (
    <ViewportDrawer open={row !== null} onClose={onClose} width="min(560px, 96vw)">
      {row ? (() => {
        const triesLabel = row.tries.length === 0 ? 'none' : `${String(row.tries.length)} so far`;
        const firstReason = row.reasons[0];
        const primary = firstReason !== undefined ? CALL_REASON_META[firstReason.type] : null;
        return (
        <div className="flex h-full flex-col bg-[var(--surface-card)]">
          {/* header */}
          <header className="border-b border-[var(--border-default)] px-6 pb-[18px] pt-5">
            <div className="flex items-start gap-3.5">
              <Avatar name={row.name} hue="var(--brand-indigo)" size={46} />
              <div className="min-w-0 flex-1">
                <h2 className="m-0 text-[21px] font-semibold leading-[1.15] tracking-[-0.025em] text-[var(--text-primary)]">{row.name}</h2>
                <div className="mt-1 font-[var(--font-mono)] text-[11.5px] font-medium text-[var(--text-secondary)]">
                  {row.phone} · {row.cardIdentity}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {primary ? (
                    <span className="rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold" style={{ background: primary.bg, color: primary.fg }}>{primary.label}</span>
                  ) : null}
                  {calling ? (
                    <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-[var(--money-green)]">
                      <span className="h-2 w-2 rounded-full bg-[var(--money-green)]" />
                      On call{callingSince ? ` since ${callingSince}` : ''}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[var(--text-muted)]">
                      <Icon kind="phone" size={12} stroke={2} /> Tries: {triesLabel}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] shadow-[var(--sh-1)] hover:bg-[var(--surface-sunken)]"
              >
                <Icon kind="x" size={15} stroke={2} />
              </button>
            </div>

            {/* actions — the call CTA, with quiet links beside it */}
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2.5">
              {calling ? (
                <Button variant="primary" size="sm" iconLeft="phone" onClick={() => { onLogCall(row); }}>Log the call</Button>
              ) : (
                <Button variant="primary" size="sm" iconLeft="phone" onClick={() => { onStartCall(row); }}>Call</Button>
              )}
              <button type="button" onClick={() => { onOpenPatient(row); }} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <Icon kind="team" size={13} stroke={2} /> Open patient
              </button>
              <button type="button" onClick={() => { onOpenCommunications(row); }} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <Icon kind="list" size={13} stroke={2} /> Communications
              </button>
            </div>
            {calling ? (
              <div className="mt-2.5 text-[11.5px] text-[var(--text-muted)]">Others can see you’re on this call.</div>
            ) : null}
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            {/* reasons */}
            <Eyebrow>{row.reasons.length} {row.reasons.length === 1 ? 'reason' : 'reasons'} to call</Eyebrow>
            <div className="mt-2.5 flex flex-col gap-3">
              {row.reasons.map((r, i) => {
                const meta = CALL_REASON_META[r.type];
                return (
                  <div
                    key={`${r.type}-${String(i)}`}
                    className="overflow-hidden rounded-[14px] border bg-[var(--surface-card)] shadow-[var(--sh-1)]"
                    style={{ borderColor: wash(meta.fg, 22) }}
                  >
                    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5" style={{ background: wash(meta.fg, 9), borderBottom: `1px solid ${wash(meta.fg, 16)}` }}>
                      <span className="inline-block h-4 w-1 rounded-full" style={{ background: meta.fg }} aria-hidden />
                      <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ color: meta.fg, background: wash(meta.fg, 14) }}>{meta.label}</span>
                      <span className="text-[13px] font-bold text-[var(--text-primary)]">{r.title}</span>
                      <button type="button" className="ml-auto text-[11.5px] font-semibold hover:underline" style={{ color: meta.fg }} onClick={() => { onOpenReason(row, meta.href(row.id)); }}>{meta.linkLabel} →</button>
                    </div>
                    <div className="px-4 py-3.5">
                      <div className="flex flex-wrap gap-1.5">
                        {r.chips.map((c) => <span key={c} className="rounded-full border border-[var(--border-default)] bg-[var(--surface-sunken)] px-2 py-0.5 text-[11px] font-semibold text-[var(--text-secondary)]">{c}</span>)}
                      </div>
                      <div className="mt-3 flex flex-col gap-2">
                        {r.whatToSay.map((w) => (
                          <div key={w.label} className="grid grid-cols-[82px_1fr] gap-3 text-[12.5px]">
                            <span className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]">{w.label}</span>
                            <span className="leading-[1.5] text-[var(--text-secondary)]">{w.body}</span>
                          </div>
                        ))}
                        <div className="grid grid-cols-[82px_1fr] gap-3 text-[12.5px]">
                          <span className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-[var(--alert-red)]">Don’t</span>
                          <span className="leading-[1.5] text-[var(--text-muted)]">{r.dont}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* what's happened — a bordered timeline */}
            <div className="mt-6 mb-3 flex items-center justify-between">
              <Eyebrow>What’s happened</Eyebrow>
              <button type="button" className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline" onClick={() => { onOpenCommunications(row); }}>Communications →</button>
            </div>
            <div className="overflow-hidden rounded-[14px] border border-[var(--border-default)] bg-[var(--surface-card)]">
              {row.timeline.map((t, i) => {
                const last = i === row.timeline.length - 1;
                return (
                  <div
                    key={`${t.at}-${String(i)}`}
                    className="grid items-start gap-3.5 px-4 py-[13px]"
                    style={{ gridTemplateColumns: 'auto 1fr auto', borderBottom: last ? 'none' : '1px solid var(--border-default)' }}
                  >
                    <span className="mt-px inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-lg bg-[var(--surface-sunken)] text-[var(--text-muted)]">
                      <Icon kind="check-circ" size={12} stroke={2} />
                    </span>
                    <span className="min-w-0 self-center text-[12.5px] leading-[1.5] text-[var(--text-secondary)]">
                      {t.body}{t.quote !== undefined ? <span className="italic text-[var(--text-primary)]"> “{t.quote}”</span> : null}
                    </span>
                    <span className="mt-1 flex-none font-[var(--font-mono)] text-[10.5px] font-medium text-[var(--text-muted)]">{t.at}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        );
      })() : null}
    </ViewportDrawer>
  );
}
